import { isElementScrolledOutsideView } from '@opten/viewport/is-element-scrolled-outside-view';

import { ScrollStrategy } from './scroll-strategy';
import { Overlay } from '../overlay';

/**
 * Config options for the RepositionScrollStrategy.
 */
// tslint:disable-next-line:interface-name
export interface RepositionScrollStrategyConfig {
	/** Time in milliseconds to throttle the scroll events. */
	scrollThrottle?: number;

	/** Whether to close the overlay once the user has scrolled away completely. */
	autoClose?: boolean;
}

/**
 * Strategy that will update the element position as the user is scrolling.
 */
export class RepositionScrollStrategy implements ScrollStrategy {
	private overlayRef: Overlay;
	private applied = false;

	constructor(private config?: RepositionScrollStrategyConfig) {}

	/** Attaches this scroll strategy to an overlay. */
	attach(overlayRef: Overlay) {
		this.overlayRef = overlayRef;
	}

	/** Enables repositioning of the attached overlay on scroll. */
	enable() {
		if (!this.applied) {
			window.addEventListener('scroll', this.handleScroll);
			this.applied = true;
		}
	}

	/** Disables repositioning of the attached overlay on scroll. */
	disable() {
		if (this.applied) {
			window.removeEventListener('scroll', this.handleScroll);
			this.applied = false;
		}
	}

	private handleScroll = () => {
		const throttle = this.config ? this.config.scrollThrottle : 0;
		const autoClose = this.config ? this.config.autoClose : true;

		const update = () => {
			this.overlayRef.updatePosition();

			if (autoClose) {
				const overlayRect = this.overlayRef.overlayElement.getBoundingClientRect();
				const { width, height } = {
					width: window.innerWidth,
					height: window.innerHeight,
				};

				// todo: include all ancestor scroll containers here once
				// we have a way of exposing the trigger element to the scroll strategy.
				const parentRects = [
					{
						width,
						height,
						bottom: height,
						right: width,
						top: 0,
						left: 0,
					},
				];

				if (isElementScrolledOutsideView(overlayRect, parentRects)) {
					this.disable();
					this.overlayRef.close();
				}
			}
		};

		if (throttle > 0) {
			setTimeout(() => update(), throttle);
		} else {
			update();
		}
	};
}
